"use client"
import type { VirtualItem } from "@tanstack/react-virtual"
import { useEffect, useRef, useState } from "react"
import type { Awaitable } from "../../types/utils"

type UseVirtualPaginationProps = {
  items: Pick<VirtualItem, "index">[]
  count: number
  loadNext?: () => Awaitable<unknown>
  hasNext?: boolean
  paginationThreshold?: number
  onError?: () => void
}

if (typeof window !== "undefined") {
  import("../../polyfills/requestIdleCallback")
}

export const useVirtualPagination = ({
  items,
  count,
  loadNext,
  hasNext,
  paginationThreshold = 1,
  onError,
}: UseVirtualPaginationProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const isLoadingRef = useRef(isLoading)
  const idleCallbackRef = useRef<number>(undefined)
  const lastItemIndex = items.at(-1)?.index

  useEffect(() => {
    if (
      !(isLoading || isLoadingRef.current) &&
      hasNext &&
      loadNext &&
      (items.length === 0 ||
        (lastItemIndex !== undefined &&
          lastItemIndex >= count - paginationThreshold))
    ) {
      isLoadingRef.current = true
      setIsLoading(true)

      Promise.resolve(loadNext())
        .then(() => {
          // Give browser time to render the things that were just fetched, so we dont re-issue the same pagination request twice
          idleCallbackRef.current = requestIdleCallback(() => {
            idleCallbackRef.current = undefined
            isLoadingRef.current = false
            setIsLoading(false)
          })
        })
        .catch(() => {
          isLoadingRef.current = false
          setIsLoading(false)
          onError?.()
        })
    }
  }, [
    count,
    hasNext,
    lastItemIndex,
    loadNext,
    paginationThreshold,
    isLoading,
    onError,
    items.length,
  ])

  useEffect(() => {
    return () => {
      if (idleCallbackRef.current) {
        cancelIdleCallback(idleCallbackRef.current)
      }
    }
  }, [])

  return isLoadingRef.current
}
